function bytesToMbps(bytes, ms) {
  if (ms <= 0) return 0;
  return (bytes * 8) / (ms / 1000) / 1_000_000;
}

function sum(values) {
  return values.reduce((total, value) => total + value, 0);
}

function makeAbortError() {
  const err = new Error('operation timed out');
  err.name = 'AbortError';
  return err;
}

function makeTimeoutController(timeoutMs) {
  if (!Number.isFinite(timeoutMs) || timeoutMs <= 0 || typeof AbortController === 'undefined') {
    return { signal: undefined, clear: () => {} };
  }
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  return {
    signal: controller.signal,
    clear: () => clearTimeout(timeoutId)
  };
}

function makeProgressReporter(onProgress, intervalMs = 250) {
  if (!onProgress) return { emit: () => {} };
  const minIntervalMs = Number.isFinite(intervalMs) && intervalMs > 0 ? intervalMs : 0;
  let lastEmitAt = -Infinity;
  let lastValue;
  return {
    emit(value, { force = false } = {}) {
      lastValue = value;
      const now = Date.now();
      if (!force && minIntervalMs && now - lastEmitAt < minIntervalMs) return;
      lastEmitAt = now;
      onProgress(lastValue);
    }
  };
}

function makeDownloadUrl(serverUrl, bytes, downloadUrl = null) {
  const nonce = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const base = downloadUrl || `${serverUrl}/download`;
  const separator = base.includes('?') ? '&' : '?';
  return `${base}${separator}bytes=${bytes}&nonce=${encodeURIComponent(nonce)}`;
}

function abortable(promise, signal) {
  if (!signal) return promise;
  if (signal.aborted) return Promise.reject(makeAbortError());
  return new Promise((resolve, reject) => {
    const onAbort = () => {
      cleanup();
      reject(makeAbortError());
    };
    const cleanup = () => signal.removeEventListener('abort', onAbort);
    signal.addEventListener('abort', onAbort, { once: true });
    promise.then(
      value => {
        cleanup();
        resolve(value);
      },
      err => {
        cleanup();
        reject(err);
      }
    );
  });
}

function wasAborted(err, signal) {
  return signal?.aborted || err?.name === 'AbortError';
}

export async function measureDownload({
  serverUrl,
  downloadUrl = null,
  bytes,
  timeoutMs = 0,
  sampleDurationMs = 0,
  progressIntervalMs = 250,
  fetchImpl = fetch,
  onProgress
}) {
  const t0 = Date.now();
  const sampleMs = Number.isFinite(sampleDurationMs) && sampleDurationMs > 0
    ? sampleDurationMs
    : 0;
  const abortAfterMs = sampleMs || timeoutMs;
  const timeout = makeTimeoutController(abortAfterMs);
  const progress = makeProgressReporter(onProgress, progressIntervalMs);
  let reader = null;
  let received = 0;

  try {
    const res = await abortable(
      fetchImpl(makeDownloadUrl(serverUrl, bytes, downloadUrl), { cache: 'no-store', signal: timeout.signal }),
      timeout.signal
    );
    if (!res.ok || !res.body) {
      throw new Error(`download failed: ${res.status}`);
    }
    reader = res.body.getReader();
    while (true) {
      const { done, value } = await abortable(reader.read(), timeout.signal);
      if (done) break;
      received += value.length;
      progress.emit(received);
    }
    const durationMs = Date.now() - t0;
    progress.emit(received, { force: true });
    return {
      requestedBytes: bytes,
      bytes: received,
      durationMs,
      mbps: bytesToMbps(received, durationMs),
      completed: true,
      timedOut: false,
      sampled: false,
      exhaustedTarget: true
    };
  } catch (err) {
    if (!wasAborted(err, timeout.signal)) throw err;
    try { await reader?.cancel?.(); } catch {}
    const durationMs = Math.max(Date.now() - t0, abortAfterMs || 0);
    const sampled = sampleMs > 0 && received > 0;
    progress.emit(received, { force: true });
    return {
      requestedBytes: bytes,
      bytes: received,
      durationMs,
      mbps: bytesToMbps(received, durationMs),
      completed: sampled,
      timedOut: !sampled,
      sampled,
      exhaustedTarget: false
    };
  } finally {
    timeout.clear();
  }
}

export async function measureUpload({
  serverUrl,
  uploadUrl = null,
  expectJson = true,
  bytes,
  timeoutMs = 0,
  fetchImpl = fetch,
  onProgress
}) {
  const payload = new Uint8Array(bytes);
  // Fill with non-zero content so any compression in the path doesn't cheat the measurement.
  for (let i = 0; i < payload.length; i++) payload[i] = (i & 0xff) ^ 0x55;

  const t0 = Date.now();
  const timeout = makeTimeoutController(timeoutMs);
  if (onProgress) onProgress({ bytes: 0, totalBytes: bytes, stage: 'started' });

  try {
    const res = await abortable(
	      fetchImpl(uploadUrl || `${serverUrl}/upload`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/octet-stream' },
        body: payload,
        signal: timeout.signal
      }),
      timeout.signal
    );
    if (!res.ok) throw new Error(`upload failed: ${res.status}`);
    if (expectJson) {
      await abortable(res.json(), timeout.signal);
    } else if (typeof res.text === 'function') {
      await abortable(res.text(), timeout.signal);
    }
    const durationMs = Date.now() - t0;
    if (onProgress) onProgress({ bytes, totalBytes: bytes, stage: 'completed' });
    return {
      requestedBytes: bytes,
      bytes,
      durationMs,
      mbps: bytesToMbps(bytes, durationMs),
      completed: true,
      timedOut: false
    };
  } catch (err) {
    if (!wasAborted(err, timeout.signal)) throw err;
    const durationMs = Math.max(Date.now() - t0, timeoutMs || 0);
    if (onProgress) onProgress({ bytes: 0, totalBytes: bytes, stage: 'timed-out' });
    return {
      requestedBytes: bytes,
      bytes: 0,
      attemptedBytes: bytes,
      durationMs,
      mbps: 0,
      completed: false,
      timedOut: true
    };
  } finally {
    timeout.clear();
  }
}

async function measureCapacity({
  direction,
  serverUrl,
  downloadUrl = null,
  uploadUrl = null,
  expectUploadJson = true,
  provider = 'convertros',
  streams = 4,
  bytesPerStream,
  timeoutMs = 15_000,
  sampleDurationMs = 0,
  progressIntervalMs = 250,
  fetchImpl = fetch,
  onProgress
}) {
  const streamCount = Math.max(1, Math.floor(streams));
  const perStreamBytes = Math.max(1, Math.floor(bytesPerStream));
  const startedAt = Date.now();
  const bytesByStream = Array(streamCount).fill(0);
  const progress = makeProgressReporter(onProgress, progressIntervalMs);

  const report = ({ force = false } = {}) => {
    progress.emit({
      direction,
      bytes: sum(bytesByStream),
      totalBytes: streamCount * perStreamBytes,
      streams: streamCount,
      elapsedMs: Math.max(0, Date.now() - startedAt),
      durationMs: sampleDurationMs || timeoutMs
    }, { force });
  };

  report();
  const workers = bytesByStream.map((_, index) => {
    if (direction === 'download') {
      return measureDownload({
        serverUrl,
        downloadUrl,
        bytes: perStreamBytes,
        timeoutMs,
        sampleDurationMs,
        progressIntervalMs,
        fetchImpl,
        onProgress: bytes => {
          bytesByStream[index] = bytes;
          report();
        }
      });
    }
    return measureUpload({
      serverUrl,
      uploadUrl,
      expectJson: expectUploadJson,
      bytes: perStreamBytes,
      timeoutMs,
      fetchImpl,
      onProgress: progress => {
        bytesByStream[index] = progress.bytes || 0;
        report();
      }
    });
  });

  const settled = await Promise.allSettled(workers);
  const durationMs = Math.max(1, Date.now() - startedAt);
  const results = settled.map((item, index) => {
    if (item.status === 'fulfilled') return item.value;
    return {
      requestedBytes: perStreamBytes,
      bytes: bytesByStream[index] || 0,
      durationMs,
      mbps: 0,
      completed: false,
      timedOut: false,
      error: item.reason?.message || String(item.reason)
    };
  });
  const bytes = results.reduce((total, result) => total + (Number(result.bytes) || 0), 0);
  const completedStreams = results.filter(result => result.completed === true).length;
  const timedOutStreams = results.filter(result => result.timedOut === true).length;
  const failedStreams = results.filter(result => result.error && result.timedOut !== true).length;
  const sampledStreams = results.filter(result => result.sampled === true).length;

  report({ force: true });
  return {
    bytes,
    durationMs,
    mbps: bytesToMbps(bytes, durationMs),
    streams: streamCount,
    bytesPerStream: perStreamBytes,
    provider,
    completedStreams,
    timedOutStreams,
    failedStreams,
    sampledStreams,
    completed: completedStreams === streamCount,
    timedOut: timedOutStreams > 0,
    sampled: sampledStreams > 0,
    results
  };
}

export async function measureCapacityDownload(options) {
  return measureCapacity({ direction: 'download', ...options });
}

export async function measureCapacityUpload(options) {
  return measureCapacity({ direction: 'upload', ...options });
}
